// src/contexts/AuthProvider.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { APP_CONFIG } from '../config/appConfig';

const getRedirectPath = (roleName) => {
  const roleMap = {
    SUPERADMIN: '/superadmin/dashboard',
    ADMIN: '/admin/dashboard',
    CLIENT: '/client/dashboard',
    ETUDIANT: '/etudiant/dashboard',
  };
  return roleMap[roleName] || '/dashboard';
};

async function fetchProfil(token) {
  const res = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error('Impossible de récupérer le profil');
  }
  return res.json();
}

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [rolesDisponibles, setRolesDisponibles] = useState([]);
  const [activeRole, setActiveRole] = useState(null);
  const [requiresRoleSelection, setRequiresRoleSelection] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyProfil = useCallback((profil) => {
    setUser({
      id: profil.id,
      email: profil.email,
      phone: profil.phone,
    });
    setRolesDisponibles(profil.rolesDisponibles || []);
    setActiveRole(profil.activeRole || null);
    setRequiresRoleSelection(!!profil.requiertSelectionRole);
  }, []);

  const clearAuthState = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setRolesDisponibles([]);
    setActiveRole(null);
    setRequiresRoleSelection(false);
  }, []);

  useEffect(() => {
    const init = async () => {
      const storedToken = localStorage.getItem('token');
      if (!storedToken) {
        setLoading(false);
        return;
      }
      try {
        const profil = await fetchProfil(storedToken);
        setToken(storedToken);
        applyProfil(profil);
      } catch (err) {
        console.error('Session invalide au chargement:', err);
        clearAuthState();
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [applyProfil, clearAuthState]);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Identifiants incorrects');
        setLoading(false);
        return { success: false, error: data.error };
      }

      if (data.mfaRequired) {
        setLoading(false);
        return {
          success: false,
          mfaRequired: true,
          tempToken: data.tempToken,
          mfaType: data.mfaType,
          userId: data.userId,
        };
      }

      if (data.accessToken) {
        localStorage.setItem('token', data.accessToken);
        setToken(data.accessToken);

        const profil = await fetchProfil(data.accessToken);
        applyProfil(profil);
        setLoading(false);

        const redirectPath = profil.requiertSelectionRole
          ? null
          : getRedirectPath(profil.activeRole?.role);

        return {
          success: true,
          requiresRoleSelection: !!profil.requiertSelectionRole,
          redirectPath,
        };
      }

      setLoading(false);
      return { success: false, error: 'Erreur inconnue' };
    } catch (err) {
      console.error('Login error:', err);
      setError('Erreur de connexion au serveur');
      setLoading(false);
      return { success: false, error: 'Erreur de connexion au serveur' };
    }
  }, [applyProfil]);

  const verifyMfa = useCallback(async (tempToken, otpCode) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/auth/verify-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken, otpCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Code OTP invalide');
        setLoading(false);
        return {
          success: false,
          error: data.error,
          remainingAttempts: data.remainingAttempts,
        };
      }

      if (data.accessToken) {
        localStorage.setItem('token', data.accessToken);
        setToken(data.accessToken);

        const profil = await fetchProfil(data.accessToken);
        applyProfil(profil);
        setLoading(false);

        const redirectPath = profil.requiertSelectionRole
          ? null
          : getRedirectPath(profil.activeRole?.role);

        return {
          success: true,
          requiresRoleSelection: !!profil.requiertSelectionRole,
          redirectPath,
        };
      }

      setLoading(false);
      return { success: false, error: 'Erreur inconnue' };
    } catch (err) {
      console.error('MFA error:', err);
      setError('Erreur de vérification MFA');
      setLoading(false);
      return { success: false, error: 'Erreur de vérification MFA' };
    }
  }, [applyProfil]);

  const resendOtp = useCallback(async (tempToken) => {
    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/auth/resend-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken }),
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error };
      }
      return { success: true, message: data.message };
    } catch (err) {
      console.error('Resend OTP error:', err);
      return { success: false, error: 'Erreur de connexion au serveur' };
    }
  }, []);

  const switchRole = useCallback(async (role, clientId) => {
    if (!token) {
      return { success: false, error: 'Non authentifié' };
    }
    try {
      const response = await fetch(`${APP_CONFIG.BASE_URL}/api/v1/auth/switch-role`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role, clientId: clientId ?? null }),
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, error: data.error || 'Changement de rôle refusé' };
      }

      localStorage.setItem('token', data.token);
      setToken(data.token);
      setActiveRole(data.activeRole);
      setRequiresRoleSelection(false);

      return {
        success: true,
        redirectPath: getRedirectPath(data.activeRole?.role),
      };
    } catch (err) {
      console.error('Switch role error:', err);
      return { success: false, error: 'Erreur de connexion au serveur' };
    }
  }, [token]);

  const logout = useCallback(() => {
    clearAuthState();
    window.location.href = '/login';
  }, [clearAuthState]);

  const refreshToken = useCallback(async () => token, [token]);

  // ⭐ NOUVEAU : Vérifie si un rôle est disponible (même s'il n'est pas actif)
  const hasAvailableRole = useCallback(
    (roleName) => {
      return rolesDisponibles.some(
        (r) => r.role === roleName || r.role === roleName.replace('ROLE_', '')
      );
    },
    [rolesDisponibles]
  );

  const value = {
    token,
    user,
    loading,
    error,
    login,
    verifyMfa,
    resendOtp,
    logout,
    refreshToken,
    isAuthenticated: !!token,
    rolesDisponibles,
    activeRole,
    requiresRoleSelection,
    switchRole,
    hasRole: useCallback(
      (roleName) =>
        activeRole?.role === roleName || activeRole?.role === roleName?.replace('ROLE_', ''),
      [activeRole]
    ),
    hasAvailableRole, // ⭐ Exposée pour les menus conditionnels
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};